import React, { useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Maximize2, ZoomIn, ZoomOut } from 'lucide-react';
import { computeLayout, autoOptimize, describeLayout } from '@/lib/bracket/layout';
import { nameRatio, uniformNameSize } from '@/lib/bracket/text';
import { isPlaceholder } from '@/lib/bracket/tree';
import { exportPdf, exportSvg, exportPng } from '@/lib/bracket/export';
import { BracketSheet } from './BracketSheet';
import { BracketToolbar } from './BracketToolbar';
import { BracketStatus } from './BracketStatus';
import { ScoreEditDialog } from './ScoreEditDialog';
import { BracketPrintPortal } from './BracketPrintPortal';
import { useBracketSettings, useFontsReady, useViewerZoom } from './useBracketHooks';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HOVER_CSS = '.bk-editable{cursor:pointer}.bk-editable:hover .bk-outline{stroke:#CF142B;stroke-width:1.4}'
  + '.bk-slot{cursor:text}.bk-slot:hover{fill:rgba(0,36,125,0.07)}';

function useNames(model, writeIn) {
  return useMemo(() => {
    const names = [];
    model.matches.forEach((m) => {
      if (writeIn || !isPlaceholder(m.team_a)) names.push(m.team_a_name || '');
      if (writeIn || !isPlaceholder(m.team_b)) names.push(m.team_b_name || '');
    });
    return names;
  }, [model, writeIn]);
}

// Write-in sheets keep the roomy blank-bracket card width; long typed names can widen it.
function useNameRatio(names, hasLogos, blank, writeIn, fontsReady) {
  return useMemo(() => {
    if (blank) return 12;
    const typed = names.filter(Boolean);
    if (writeIn) return typed.length ? Math.max(12, nameRatio(typed, hasLogos)) : 12;
    return names.length ? nameRatio(names, hasLogos) : 9;
  }, [names, hasLogos, blank, writeIn, fontsReady]); // eslint-disable-line react-hooks/exhaustive-deps
}

const EXPORTERS = { pdf: exportPdf, svg: exportSvg, png: exportPng };

const ZOOM_BTN = 'h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:border-[#00247D] hover:text-[#00247D] transition-colors';
const CompactZoom = ({ zoom }) => (
  <div className="flex items-center justify-end gap-1.5" data-testid="bracket-compact-zoom">
    <span className="text-xs text-slate-500 mr-1 tabular-nums" data-testid="bracket-compact-zoom-pct">{zoom.pct}%</span>
    <button type="button" className={ZOOM_BTN} onClick={() => zoom.zoomBy(1 / 1.25)} aria-label="Zoom out" data-testid="bracket-compact-zoom-out"><ZoomOut className="w-4 h-4" /></button>
    <button type="button" className={ZOOM_BTN} onClick={() => zoom.zoomBy(1.25)} aria-label="Zoom in" data-testid="bracket-compact-zoom-in"><ZoomIn className="w-4 h-4" /></button>
    <button type="button" className={ZOOM_BTN} onClick={() => zoom.setMode('width')} aria-label="Fit to width" data-testid="bracket-compact-zoom-fit"><Maximize2 className="w-4 h-4" /></button>
  </div>
);

// `custom` (Bracket Library customization): { writeIn, champNames, onSlot, watermark, gate(kind), printFallback, canEdit, onLockedEdit }
// `statsLinks` ({ matchId: href }) adds a box-score icon to games with published stats (screen only).
export const BracketBoard = React.forwardRef(({
  model, title, subtitle, eventLogo, logos, editable, onScoreSave, storageKey, fileName = 'bracket', allowBlank, showCourt,
  custom, onCustomize, baseSettings, onSettingsChange, statsLinks, compact, cricket,
}, ref) => {
  const [settings, update] = useBracketSettings(storageKey, model, baseSettings);
  const settingsKey = JSON.stringify(settings);
  useEffect(() => { onSettingsChange?.(JSON.parse(settingsKey)); }, [settingsKey, onSettingsChange]);
  const fontsReady = useFontsReady();
  const [blankState, setBlank] = useState(false);
  const writeIn = !!custom?.writeIn;
  const blank = blankState && !writeIn;
  const names = useNames(model, writeIn);
  const hasLogos = Object.keys(logos || {}).length > 0;
  const ratio = useNameRatio(names, hasLogos, blank, writeIn, fontsReady);
  const layout = useMemo(() => computeLayout(model, settings, { ratio }), [model, settings, ratio]);
  const nameFs = useMemo(() => uniformNameSize(names, layout.M, hasLogos), [names, layout, hasLogos, fontsReady]); // eslint-disable-line react-hooks/exhaustive-deps
  const viewerRef = useRef(null);
  const sheetRefs = useRef([]);
  const printRefs = useRef([]);
  const zoom = useViewerZoom(viewerRef, layout.page);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const logoUrl = useCallback((teamId) => (logos?.[teamId] ? `${API_URL}/api/files/${logos[teamId]}` : null), [logos]);
  const sheetProps = { title, subtitle, eventLogo, logoUrl, blank, showCourt, uid, nameFs, writeIn, champNames: custom?.champNames };
  // Without Pro, printing a customized bracket (incl. Ctrl+P) outputs the blank write-in version.
  const printProps = custom?.printFallback ? { ...sheetProps, ...custom.printFallback, blank: true, writeIn: false } : sheetProps;

  useEffect(() => {
    if (!custom?.printFallback || !custom.onBlockedPrint) return undefined;
    window.addEventListener('beforeprint', custom.onBlockedPrint);
    return () => window.removeEventListener('beforeprint', custom.onBlockedPrint);
  }, [custom]);

  const optimize = () => {
    const best = autoOptimize(model, settings, { ratio });
    update(best.settings);
    toast.success('Layout optimized', { description: describeLayout(best.layout) });
  };

  const download = async (kind, els) => {
    setBusy(kind);
    try {
      await EXPORTERS[kind](els, layout.page, fileName);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`Could not export ${kind.toUpperCase()}`, { description: e?.message });
    } finally { setBusy(null); }
  };

  const runExport = async (kind) => {
    if (custom?.gate && !(await custom.gate(kind))) return;
    if (kind === 'print') { window.print(); return; }
    await download(kind, sheetRefs.current.slice(0, layout.pageCount).filter(Boolean));
  };

  useImperativeHandle(ref, () => ({
    run: runExport,
    // The blank write-in version, always free.
    downloadBlank: () => download('pdf', printRefs.current.slice(0, layout.pageCount).filter(Boolean)),
  }));

  return (
    <div data-testid="bracket-board">
      <style x-excluded="true">{HOVER_CSS}</style>
      {compact ? <CompactZoom zoom={zoom} /> : (
        <>
          <BracketToolbar settings={settings} update={update} layout={layout} model={model} zoom={zoom} onOptimize={optimize}
            onExport={runExport} busy={busy} allowBlank={allowBlank && !writeIn} blank={blank} setBlank={setBlank} onCustomize={onCustomize} />
          <BracketStatus layout={layout} nameFs={nameFs} settings={settings} update={update} editable={!!editable && !!onScoreSave} />
        </>
      )}
      <div ref={viewerRef} data-testid="bracket-viewer" className="mt-3 rounded-2xl border border-slate-200 bg-[#E6EAF1] overflow-auto"
        style={{ backgroundImage: 'radial-gradient(#cfd6e2 1px, transparent 1px)', backgroundSize: '18px 18px' }}>
        <div className="p-4 flex flex-col items-center gap-6 w-max min-w-full">
          {layout.geom.viewports.map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="bg-white rounded-[3px] shadow-[0_18px_40px_-18px_rgba(15,23,42,0.45)] animate-in fade-in duration-300">
              <BracketSheet ref={(el) => { sheetRefs.current[i] = el; }} layout={layout} index={i} {...sheetProps}
                width={layout.page.w * zoom.z} height={layout.page.h * zoom.z} onSelect={editable && onScoreSave ? setEditing : custom?.onLockedEdit || null}
                onSlot={custom?.onSlot} watermark={custom?.watermark || null} canEdit={custom?.canEdit} statsLinks={statsLinks} />
            </div>
          ))}
        </div>
      </div>
      <ScoreEditDialog match={editing} onClose={() => setEditing(null)} onSave={onScoreSave} cricket={cricket} />
      <BracketPrintPortal layout={layout} sheetProps={printProps} sheetRefs={printRefs} />
    </div>
  );
});

BracketBoard.displayName = 'BracketBoard';
